import { IPromise, IScope } from "angular";
import { ITeam, TeamService } from "@gtmhub/teams";
import { IEmployee } from "@gtmhub/employees/models/models";
import { EmployeeService } from "@gtmhub/employees/services/employeeService";
import { IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { TeamEvents } from "../events";
import { IRestLayerRequest } from "@gtmhub/core";
import { IdMap, unique } from "@gtmhub/util";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { AssigneeActions, formatSearchAssignees, IAssigneesStoreState } from "@gtmhub/assignees";
import { INgRedux } from "@gtmhub/state-management";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { SearchService } from "@gtmhub/shared/services/search.service";
import { SearchOperatorsEnum } from "@webapp/search/models/search-api.models";
import { Search } from "@webapp/search/models/search.models";

const dropContainerSelector = ".drop-container";
const readyToDropClass = "ready-to-drop";

interface ITeamsRedux {
  assigneesIdMap: IdMap<ITeam>;
  assigneesFetched: boolean;
  assigneesError: IUIError;
}

const bindStateToScope = (state: IAssigneesStoreState): ITeamsRedux => {
  return {
    assigneesIdMap: state.assignees.map,
    assigneesFetched: state.assignees.isFetched,
    assigneesError: state.assignees.error,
  };
};

type ActiveTab = "allEmployees" | "employeesWithoutTeams";

export type Paginator = {
  currentPage: number;
  itemsPerPage: number;
  paginatorLimit: number;
  totalPages?: number;
  totalCount?: number;
};

export interface IManageTeamsScope extends IScope, IExtendedRootScopeService, ITeamsRedux {
  teams: ITeam[];
  users: IEmployee[];
  indicators: {
    loadingTeams?: boolean;
    search?: boolean;
  };
  activeTab: ActiveTab;
  employeePaginator: Paginator;
  teamPaginator: Paginator;
  employeeSearch: string;
  teamSearch: string;
  setTeamManager(newManager: IEmployee, team: ITeam): void;
  removeTeamManager(team: ITeam): void;
  addTeamMember(newMember: IEmployee, team: ITeam): void;
  removeTeamMember(team: ITeam, memberId: string): void;
  onPageChange(page: number): void;
  changeActiveTab(tab: ActiveTab): void;
  clearSearch(paginator: "employee" | "team"): void;
  searchEmployees(employeeName: string): void;
  searchTeams(teamSearch: string): void;
  userTeamsCount(user: IEmployee): number;
  onDragEmployeeStart(): void;
  onDragEmployeeEnd(): void;
  verifyEmployeeIsDragged($dragData: unknown): boolean;
}

export class ManageTeamsCtrl {
  public static $inject = ["$scope", "$ngRedux", "AssigneeActions", "TeamService", "EmployeeService", "UIErrorHandlingService", "$rootScope", "SearchService"];

  constructor(
    private $scope: IManageTeamsScope,
    $ngRedux: INgRedux,
    assigneeActions: AssigneeActions,
    private teamService: TeamService,
    private employeeService: EmployeeService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private $rootScope: ITraceRootScopeService,
    private searchService: SearchService
  ) {
    this.$scope.indicators = {};

    this.teamEvents = new TeamEvents($scope);

    this.$scope.setTeamManager = this.setTeamManager;
    this.$scope.removeTeamManager = this.removeTeamManager;
    this.$scope.addTeamMember = this.addTeamMember;
    this.$scope.removeTeamMember = this.removeTeamMember;
    this.$scope.userTeamsCount = this.userTeamsCount;
    this.$scope.changeActiveTab = this.changeActiveTab;
    this.$scope.onPageChange = this.onPageChange;
    this.$scope.clearSearch = this.clearSearch;
    this.$scope.searchEmployees = this.searchEmployees;
    this.$scope.searchTeams = this.searchTeams;
    this.$scope.onDragEmployeeStart = this.onDragEmployeeStart;
    this.$scope.onDragEmployeeEnd = this.onDragEmployeeEnd;
    this.$scope.verifyEmployeeIsDragged = this.verifyEmployeeIsDragged;

    this.$scope.activeTab = "allEmployees";
    this.setDefaultPaginator("employee");
    this.setDefaultPaginator("team");

    this.fetchEmployees(this.$scope.activeTab, this.$scope.employeeSearch);
    const unsubscribe = $ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", unsubscribe);
    $ngRedux.dispatch(assigneeActions.getAssignees());

    this.$scope.indicators.loadingTeams = true;

    this.$scope
      .watchUntilAndRejectOnError(
        () => this.$scope.assigneesFetched,
        () => this.$scope.assigneesError
      )
      .then(
        () => {
          this.getTeams(this.$scope.teamSearch);
        },
        (error) => this.uiErrorHandlingService.handleIndicator(error, { indicator: "loadingTeams", inline: true })
      );
  }

  private teamEvents: TeamEvents;

  private onPageChange = (page: number, paginator: "employee" | "team" = "employee"): void => {
    this.$rootScope.traceAction("change_page", () => {
      if (paginator === "employee") {
        this.$scope.employeePaginator.currentPage = page;
        this.fetchEmployees(this.$scope.activeTab, this.$scope.employeeSearch);
      }
      if (paginator === "team") {
        this.$scope.teamPaginator.currentPage = page;
        this.getTeams(this.$scope.teamSearch);
      }
    });
  };

  private setDefaultPaginator = (paginator: "employee" | "team" = "employee"): void => {
    if (paginator === "employee") {
      this.$scope.employeePaginator = {
        currentPage: 1,
        itemsPerPage: 20,
        paginatorLimit: 5,
      };
    }

    if (paginator === "team") {
      this.$scope.teamPaginator = {
        currentPage: 1,
        itemsPerPage: 5,
        paginatorLimit: 3,
      };
    }
  };

  private clearSearch = (paginator: "employee" | "team"): void => {
    this.$rootScope.traceAction("clear_search", () => {
      if (paginator === "employee") {
        this.$scope.employeeSearch = "";
        this.setDefaultPaginator("employee");
        this.fetchEmployees(this.$scope.activeTab, this.$scope.employeeSearch);
      }

      if (paginator === "team") {
        this.$scope.teamSearch = "";
        this.setDefaultPaginator("team");
        this.getTeams(this.$scope.teamSearch);
      }
    });
  };

  private changeActiveTab = (tab: ActiveTab): void => {
    this.$rootScope.traceAction("change_active_tab", () => {
      this.$scope.activeTab = tab;

      this.clearSearch("employee");
    });
  };

  private fetchEmployees = (activeTab: ActiveTab, employeeSearch: string): void => {
    if (activeTab === "allEmployees") {
      this.getEmployees("search", false, employeeSearch);
    }

    if (activeTab === "employeesWithoutTeams") {
      this.getEmployees("search", true, employeeSearch);
    }
  };

  private searchTeams = (teamSearch: string): void => {
    this.$rootScope.traceAction("search_teams", () => {
      this.$scope.teamSearch = teamSearch;

      this.setDefaultPaginator("team");
      this.getTeams(this.$scope.teamSearch);
    });
  };

  private searchEmployees = (employeeSearch: string): void => {
    this.$rootScope.traceAction("search_employees", () => {
      this.$scope.employeeSearch = employeeSearch;

      this.setDefaultPaginator("employee");
      this.fetchEmployees(this.$scope.activeTab, this.$scope.employeeSearch);
    });
  };

  private setTeamManager = (newManager: IEmployee, team: ITeam): void => {
    this.$rootScope.traceAction("set_team_manager", () => {
      this.teamService.setTeamManager(team.id, newManager.id).then(() => {
        this.addUserAsManager(team, newManager);
        this.teamEvents.broadcastTeamUpdated({ team });
      });
    });
  };

  private removeTeamManager = (team: ITeam): void => {
    this.$rootScope.traceAction("remove_team_manager", () => {
      this.teamService.removeTeamManager(team.id).then(() => {
        this.removeManagerFromTeam(team);
        this.teamEvents.broadcastTeamUpdated({ team });
      });
    });
  };

  private addTeamMember = (newMember: IEmployee, team: ITeam): void => {
    this.$rootScope.traceAction("add_team_member", () => {
      team.members = team.members || [];
      if (!team.members.includes(newMember.id)) {
        this.teamService.addTeamMember(team.id, [newMember.id]).then(() => {
          this.addUserAsTeamMember(team, newMember);
          this.teamEvents.broadcastTeamUpdated({ team });
        });
      }
    });
  };

  private removeTeamMember = (team: ITeam, memberId: string): void => {
    this.$rootScope.traceAction("remove_team_member", () => {
      this.teamService.removeTeamMember(team.id, memberId).then(() => {
        this.removeEmployeeFromTeam(team, memberId);
        this.teamEvents.broadcastTeamUpdated({ team });
      });
    });
  };

  private removeUserFromList = (userId: string): void => {
    if (this.$scope.employeePaginator.totalPages === 1) {
      this.$scope.users = this.$scope.users.filter((user) => user.id !== userId);
    } else {
      if (this.$scope.employeePaginator.currentPage > 1 && this.$scope.users.length === 1) {
        this.$scope.employeePaginator.currentPage--;
      }

      this.reloadUsersList();
    }
  };

  private reloadUsersList = (): void => {
    this.getEmployees("search", true, this.$scope.employeeSearch);
  };

  private addUserAsTeamMember = (team: ITeam, newMember: IEmployee): void => {
    team.members.push(newMember.id);
    team.membersInfo = team.membersInfo || [];
    team.membersInfo.push(newMember);

    newMember.teamIds = newMember.teamIds || [];
    newMember.teamIds.push(team.id);

    if (this.$scope.activeTab === "employeesWithoutTeams") {
      this.removeUserFromList(newMember.id);
    }
  };

  private userTeamsCount = (user: IEmployee): number => {
    const userTeamsIds = user.teamIds || [];
    const manageTeamsIds = user.managedTeamIds || [];

    return unique([...userTeamsIds, ...manageTeamsIds]).length;
  };

  private removeManagerFromTeam = (team: ITeam): void => {
    const teamMananagerIdToRemove = team.manager;
    const teamMananagerToRemove = this.$scope.users.find((employee) => employee.id === teamMananagerIdToRemove);
    if (teamMananagerToRemove && Array.isArray(teamMananagerToRemove.managedTeamIds)) {
      teamMananagerToRemove.managedTeamIds = teamMananagerToRemove.managedTeamIds.filter((teamId) => teamId !== team.id);
    }
    team.manager = "";
    team.managerInfo = null;

    if (this.$scope.activeTab === "employeesWithoutTeams") {
      this.reloadUsersList();
    }
  };

  private addUserAsManager = (team: ITeam, newManager: IEmployee): void => {
    team.manager = newManager.id;
    team.managerInfo = newManager;

    newManager.managedTeamIds = newManager.managedTeamIds || [];
    newManager.managedTeamIds.push(team.id);

    if (this.$scope.activeTab === "employeesWithoutTeams") {
      this.removeUserFromList(newManager.id);
    }
  };

  private removeEmployeeFromTeam = (team: ITeam, removedMemberId: string): void => {
    team.members = team.members.filter((memberId) => memberId !== removedMemberId);
    team.membersInfo = team.membersInfo?.filter((member) => member.id !== removedMemberId);

    const removedMember = this.$scope.users.find((employee) => employee.id === removedMemberId);
    if (removedMember && Array.isArray(removedMember.teamIds)) {
      removedMember.teamIds = removedMember.teamIds.filter((teamId) => teamId !== team.id);
    }

    if (this.$scope.activeTab === "employeesWithoutTeams") {
      this.reloadUsersList();
    }
  };

  private getTeams = (searchTerm: string): IPromise<unknown> => {
    this.$scope.indicators.loadingTeams = true;

    const searchRequests = this.searchService.getAssigneeSearchCollections("team", searchTerm);

    const queryParams = {
      skip: (this.$scope.teamPaginator.currentPage - 1) * this.$scope.teamPaginator.itemsPerPage,
      take: this.$scope.teamPaginator.itemsPerPage,
    };

    const searchBody = {
      searchTerm,
      operator: searchTerm ? SearchOperatorsEnum.matchPrefix : SearchOperatorsEnum.matchAny,
      searchRequests,
    };

    return this.searchService.getSomeSearchData<Search<"teams">>(searchBody, queryParams).then(
      (response) => {
        this.$scope.indicators.loadingTeams = false;
        this.$scope.teams = formatSearchAssignees(response.items);
        this.$scope.teamPaginator.totalPages = Math.ceil(response.totalCount / this.$scope.teamPaginator.itemsPerPage);
        this.$scope.teamPaginator.totalCount = response.totalCount;
      },
      (error) => {
        this.uiErrorHandlingService.handleIndicator(error, { indicator: "loadingTeams", inline: true });
      }
    );
  };

  private getEmployees = (indicator = "loadingEmployees", onlyNotInTeam: boolean, employeeSearch: string): IPromise<unknown> => {
    this.$scope.indicators[indicator] = true;

    const queryParams: IRestLayerRequest = {
      skip: (this.$scope.employeePaginator.currentPage - 1) * this.$scope.employeePaginator.itemsPerPage,
      limit: this.$scope.employeePaginator.itemsPerPage,
      fields: "picture,email,firstName,lastName,teamIds,managedTeamIds,isActive",
    };

    if (employeeSearch) {
      // map of all special regex characters which might be included in a user's name
      const regexToChar = { " ": "|", "+": "\\+", "?": "\\?", "!": "\\!", ":": "\\:", ".": "\\.", "^": "\\^", $: "\\$", "-": "\\-" };
      // replace all occurences of special regex characters in a user's name from the above map
      const employeeRegexxed = `${employeeSearch.replace(/[ |+?!:.^$-]/, (m) => regexToChar[m])}`;
      const paramToSearch = `^(${employeeRegexxed})|(${employeeRegexxed})$`;
      queryParams.filter = {
        $or: [{ firstName: { $regex: paramToSearch } }, { lastName: { $regex: paramToSearch } }, { email: { $regex: paramToSearch } }],
      };
    }

    if (onlyNotInTeam) {
      queryParams.filter = {
        ...queryParams.filter,
        $and: [
          {
            $or: [{ teamIds: { $exists: false } }, { teamIds: [] }],
          },
          {
            $or: [{ managedTeamIds: { $exists: false } }, { managedTeamIds: [] }],
          },
        ],
      };
    }

    return this.employeeService.getEmployeesV2(queryParams).then(
      (employeesCollection) => {
        this.$scope.indicators[indicator] = false;

        this.$scope.users = employeesCollection.items;
        this.$scope.employeePaginator.totalPages = Math.ceil(employeesCollection.totalCount / this.$scope.employeePaginator.itemsPerPage);
        this.$scope.employeePaginator.totalCount = employeesCollection.totalCount;
      },
      (error) => {
        this.uiErrorHandlingService.handleIndicator(error, { indicator, inline: true });
      }
    );
  };

  private onDragEmployeeStart = (): void => {
    document.querySelectorAll(dropContainerSelector).forEach((el) => el.classList.add(readyToDropClass));
  };

  private onDragEmployeeEnd = (): void => {
    document.querySelectorAll(dropContainerSelector).forEach((el) => el.classList.remove(readyToDropClass));
  };

  private verifyEmployeeIsDragged = ($dragData: unknown): boolean => {
    const draggedItemId: string = $dragData && $dragData["id"] ? $dragData["id"] : "";
    return this.$scope.users.some((user) => user.id === draggedItemId);
  };
}
